import React, { useMemo } from "react"
import { Button } from "~/ui-rtk/components/ui/controls"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { TableCellsIcon } from "@heroicons/react/24/outline"
import useExportTableAccess from "~/ui-rtk/hooks/export-table-access"
import {
  useChartDataExportWorker,
  ChartDataPoint,
} from "~/ui-rtk/hooks/use-chart-data-export-worker"

export interface ChartDataExportButtonProps {
  data: ChartDataPoint[]
  metrics: string[]
  dateKey?: string
  compareMetrics?: string[]
  compareDateKey?: string
  filename?: string
  disabled?: boolean
  className?: string
  size?: "sm" | "md" | "lg"
  children?: React.ReactNode
}

export const ChartDataExportButton: React.FC<ChartDataExportButtonProps> = ({
  data,
  metrics,
  dateKey,
  compareMetrics,
  compareDateKey,
  filename,
  disabled = false,
  className,
  size = "sm",
  children = "Export Chart Data",
}) => {
  const isExportTableAccess = useExportTableAccess()
  const { exportChartDataToCSV, isExporting, progress } =
    useChartDataExportWorker()

  // Pre-extract compare data from the merged data on the React side
  // where we have full access to the data structure
  const compareData = useMemo(() => {
    if (!compareMetrics || !compareDateKey || !data?.length) return undefined

    return data
      .filter(item => item[compareDateKey] != null)
      .map(item => {
        const point: ChartDataPoint = {
          date: item[compareDateKey] as string,
        }
        for (const metric of compareMetrics) {
          // Strip "compared." prefix for the output key
          const cleanKey = metric.replace(/^compared\./, "")
          point[cleanKey] = item[metric]
        }
        return point
      })
  }, [data, compareMetrics, compareDateKey])

  // Clean up current period metric keys (strip "compared." prefix if present)
  const cleanMetrics = useMemo(
    () => metrics.map(m => m.replace(/^compared\./, "")),
    [metrics],
  )

  // Build current period data with clean keys and correct date
  const currentData = useMemo(() => {
    if (!data?.length) return data
    const needsRemap =
      metrics.some(m => m.startsWith("compared.")) ||
      (dateKey && dateKey !== "date")
    if (!needsRemap) return data

    return data.map(item => {
      const point: ChartDataPoint = { ...item }
      // Remap date if current period dates are under a different key
      if (dateKey && dateKey !== "date") {
        point.date = item[dateKey] as string
      }
      for (let i = 0; i < metrics.length; i++) {
        if (metrics[i] !== cleanMetrics[i]) {
          point[cleanMetrics[i]] = item[metrics[i]]
        }
      }
      return point
    })
  }, [data, metrics, cleanMetrics, dateKey])

  if (!isExportTableAccess) {
    return null
  }

  const handleExport = () => {
    if (disabled || isExporting || !data || !metrics) return

    exportChartDataToCSV(currentData, cleanMetrics, {
      filename,
      compareData,
    })
  }

  const buttonText =
    isExporting && progress
      ? `${progress.message || "Exporting..."} ${Math.round(progress.progress)}%`
      : children

  return (
    <Button
      onClick={handleExport}
      disabled={disabled || isExporting || !data?.length || !metrics?.length}
      size={size}
      variant={{ variant: "outlined" }}
      className={cn("flex items-center gap-2", className)}
    >
      <TableCellsIcon className="h-4 w-4" />
      {buttonText}
    </Button>
  )
}

export default ChartDataExportButton
